/**
 * WhatsNewProvider Context
 *
 * Manages the What's New modal state globally, including:
 * - Modal visibility state
 * - Feature configuration loading and caching
 * - Version-aware modal display logic
 * - User dismissal persistence via localStorage
 *
 * Usage:
 * 1. Wrap your app root with <WhatsNewProvider>
 * 2. Access modal state with useWhatsNew() hook in any child component
 * 3. Call openModal(), closeModal(), or dismissModal() to control the modal
 */

import React, { createContext, useContext, useState, useEffect } from "react";
import { FeatureConfig } from "../types/featureConfig";
import { loadFeatureConfig } from "../utils/featureConfigLoader";
import {
  hasUserDismissedModal,
  markModalAsDismissed,
  getCurrentFeatureVersion,
  isNewVersionAvailable,
  getLastSeenVersion,
  markVersionAsSeen,
} from "../services/whatsNewService";
import WhatsNewModal from "../components/WhatsNewModal";
import { useAuth } from "./AuthContext";

/**
 * WhatsNewContextType interface defines the shape of the context
 *
 * @interface WhatsNewContextType
 * @property showModal - Whether the modal should be displayed
 * @property currentVersion - Current feature version string
 * @property featureConfig - Loaded feature configuration (null while loading)
 * @property openModal - Function to open the modal
 * @property closeModal - Function to close the modal without dismissing
 * @property dismissModal - Function to close and persist dismissal in localStorage
 */
export interface WhatsNewContextType {
  /** Controls modal visibility */
  showModal: boolean;
  /** Current feature version from configuration */
  currentVersion: string;
  /** Feature configuration with release info and features */
  featureConfig: FeatureConfig | null;
  /** Opens the modal (shows it) */
  openModal: () => void;
  /** Closes the modal without marking as dismissed */
  closeModal: () => void;
  /** Closes the modal and marks it as dismissed for the current version */
  dismissModal: () => void;
}

/**
 * Create the React Context for What's New modal state
 * Default value is undefined to enforce usage within provider
 */
export const WhatsNewContext = createContext<WhatsNewContextType | undefined>(
  undefined
);

/**
 * Custom hook to access WhatsNewContext
 * Must be used within a WhatsNewProvider
 *
 * @returns WhatsNewContextType with all modal state and control functions
 * @throws Error if used outside of WhatsNewProvider
 */
export const useWhatsNew = (): WhatsNewContextType => {
  const context = useContext(WhatsNewContext);
  if (context === undefined) {
    throw new Error(
      "useWhatsNew must be used within a <WhatsNewProvider>. " +
        "Please wrap your component tree with <WhatsNewProvider> at the root level."
    );
  }
  return context;
};

/**
 * WhatsNewProvider component - manages What's New modal state and feature config
 *
 * Loads feature configuration on mount and automatically displays the modal if:
 * 1. Feature config is successfully loaded
 * 2. User has not previously dismissed the modal for this version
 *
 * @param children - React components to wrap with the provider
 */
export const WhatsNewProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  // Initialize showModal to false - we'll check dismissal after loading config
  // This prevents checking against the wrong version (getCurrentFeatureVersion returns "1.0.0"
  // but we need to check against config.version which is "1.3.0")
  const [showModal, setShowModal] = useState<boolean>(false);
  const [featureConfig, setFeatureConfig] = useState<FeatureConfig | null>(
    null
  );
  const { currentUser } = useAuth();

  /**
   * Load feature configuration on component mount
   * We don't show the modal immediately - we wait for authentication
   */
  useEffect(() => {
    const loadFeatureConfigData = async () => {
      try {
        // Path to the feature configuration JSON file
        // This path should be relative to the public directory or absolute URL
        const configPath = "/features-config.json";
        const config = await loadFeatureConfig(configPath);
        setFeatureConfig(config);
      } catch (error) {
        // Log error but don't crash the app
        if (error instanceof Error) {
          console.warn(
            `[WhatsNewProvider] Failed to load feature config: ${error.message}`
          );
        } else {
          console.warn("[WhatsNewProvider] Failed to load feature config");
        }
      }
    };

    loadFeatureConfigData();
  }, []);

  /**
   * Check for modal dismissal and show modal after user is authenticated
   */
  useEffect(() => {
    // Only check dismissal and potentially show modal when:
    // 1. Feature config is loaded
    // 2. User is authenticated
    if (featureConfig && currentUser) {
      const currentVersion = featureConfig.version;
      // Check if user has already dismissed this specific version
      const userDismissed = hasUserDismissedModal(currentVersion);
      
      // DEBUG: Log dismissal check
      console.log("[WhatsNewProvider] Checking dismissal after login:", {
        version: currentVersion,
        key: `whats_new_modal_dismissed_v${currentVersion}`,
        rawValue: localStorage.getItem(`whats_new_modal_dismissed_v${currentVersion}`),
        userDismissed,
        willShowModal: !userDismissed,
        currentUser: currentUser.email,
      });

      // Show modal only if user hasn't dismissed THIS specific version
      if (!userDismissed) {
        console.log(
          "[WhatsNewProvider] Showing modal after login - user has not dismissed"
        );
        // Mark this version as seen immediately when we show the modal
        markVersionAsSeen(currentVersion);
        setShowModal(true);
      } else {
        console.log(
          "[WhatsNewProvider] Not showing modal - user has dismissed"
        );
        setShowModal(false);
      }
    }
  }, [featureConfig, currentUser]);

  /**
   * Open the modal (show it)
   */
  const openModal = (): void => {
    setShowModal(true);
  };

  /**
   * Close the modal without marking as dismissed
   * User can open it again via openModal
   */
  const closeModal = (): void => {
    setShowModal(false);
  };

  /**
   * Close the modal and mark it as dismissed for the current version
   * This persists the dismissal in localStorage so it won't show again
   * until the version changes
   */
  const dismissModal = (): void => {
    if (featureConfig) {
      markModalAsDismissed(featureConfig.version);
    }
    setShowModal(false);
  };

  // Get current version for context value
  const currentVersion = featureConfig?.version || getCurrentFeatureVersion();

  const contextValue: WhatsNewContextType = {
    showModal,
    currentVersion,
    featureConfig,
    openModal,
    closeModal,
    dismissModal,
  };

  return (
    <WhatsNewContext.Provider value={contextValue}>
      {children}
      {/* Only render modal if featureConfig exists AND showModal is true */}
      {/* This prevents React StrictMode from keeping stale modal instances in DOM */}
      {featureConfig && showModal && (
        <WhatsNewModal
          open={showModal}
          onClose={dismissModal}
          featureConfig={featureConfig}
        />
      )}
    </WhatsNewContext.Provider>
  );
};
